import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useData } from '../context/DataContext';
import { useToast } from '../components/Toast';

const SUCURSALES = [
  { key: 'monterrey', label: 'Monterrey' },
  { key: 'zacatecas', label: 'Zacatecas' },
  { key: 'queretaro', label: 'Querétaro' },
];

const VACIO = {
  id: '', nombre_producto: '', categoria: '', descripcion: '',
  precio: '', colores_disponibles: '', foto_url: '',
  monterrey: 'SI', zacatecas: 'NO', queretaro: 'NO',
};

function SiNo({ value }) {
  const si = String(value || '').trim().toUpperCase() === 'SI';
  return (
    <span style={{
      padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700,
      background: si ? '#dcfce7' : '#fee2e2', color: si ? '#15803d' : '#dc2626',
    }}>{si ? 'SI' : 'NO'}</span>
  );
}

// Reescala la foto antes de convertirla a base64. Es obligatorio: la subida
// viaja por query params (JSONP-POST) y una foto de cámara sin reducir excede
// el límite de longitud de URL.
function resizeImage(file, maxPx = 600, quality = 0.75) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const scale = Math.min(maxPx / img.width, maxPx / img.height, 1);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

function ModalProducto({ modo, inicial, onClose, onGuardar }) {
  const { subirFotoStock } = useData();
  const toast = useToast();
  const fileRef = useRef();

  const [form, setForm] = useState(inicial ? { ...VACIO, ...inicial } : VACIO);
  const [subiendo, setSubiendo] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleFoto = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.show('Solo se aceptan imágenes', 'error'); return; }
    setSubiendo(true);
    try {
      const dataUrl = await resizeImage(file, 600, 0.75);
      const [meta, base64] = dataUrl.split(',');
      const mimeType = meta.split(':')[1].split(';')[0];
      const url = await subirFotoStock(base64, file.name, mimeType);
      if (!url) throw new Error('sin url');
      set('foto_url', url);
      toast.show('Foto subida');
    } catch (err) {
      toast.show('No se pudo subir la foto', 'error');
    } finally {
      setSubiendo(false);
      e.target.value = '';
    }
  };

  const guardar = async () => {
    if (!form.nombre_producto.trim()) { toast.show('El nombre del producto es obligatorio', 'error'); return; }
    setGuardando(true);
    try { await onGuardar(form); onClose(); }
    catch (e) { toast.show('No se pudo guardar', 'error'); }
    finally { setGuardando(false); }
  };

  const L = ({ children }) => (
    <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', margin: '10px 0 4px' }}>{children}</label>
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 900,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div className="card" style={{ width: '100%', maxWidth: 460, padding: 22, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>{modo === 'nuevo' ? 'Nuevo producto' : 'Editar producto'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, color: '#94a3b8' }}>×</button>
        </div>

        <L>Nombre del producto *</L>
        <input type="text" value={form.nombre_producto} onChange={e => set('nombre_producto', e.target.value)}
          placeholder="ej. Ventana corrediza linea premium" />

        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <L>Clave</L>
            <input type="text" value={form.id} onChange={e => set('id', e.target.value)} placeholder="ACM-006" />
          </div>
          <div style={{ flex: 1 }}>
            <L>Precio (MXN)</L>
            <input type="number" value={form.precio} onChange={e => set('precio', e.target.value)} placeholder="4200" />
          </div>
        </div>

        <L>Categoría</L>
        <input type="text" value={form.categoria} onChange={e => set('categoria', e.target.value)} placeholder="Ventanas" />

        <L>Descripción</L>
        <input type="text" value={form.descripcion} onChange={e => set('descripcion', e.target.value)}
          placeholder="Perfil europeo con vidrio filtrasol 6mm" />

        <L>Colores disponibles</L>
        <input type="text" value={form.colores_disponibles} onChange={e => set('colores_disponibles', e.target.value)}
          placeholder="Blanco / Negro" />

        <L>Existencia por sucursal</L>
        <div style={{ display: 'flex', gap: 10 }}>
          {SUCURSALES.map(s => (
            <div key={s.key} style={{ flex: 1 }}>
              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>{s.label}</div>
              <select value={form[s.key]} onChange={e => set(s.key, e.target.value)}>
                <option value="SI">SI</option>
                <option value="NO">NO</option>
              </select>
            </div>
          ))}
        </div>

        <L>Foto del producto</L>
        <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFoto} />
        <button
          onClick={() => fileRef.current && fileRef.current.click()}
          disabled={subiendo}
          style={{
            width: '100%', padding: '9px 16px', fontSize: 13, fontWeight: 600,
            background: subiendo ? '#e2e8f0' : '#eff6ff', color: subiendo ? '#94a3b8' : '#185fa5',
            border: '1px dashed ' + (subiendo ? '#cbd5e1' : '#185fa5'), borderRadius: 8,
          }}>
          {subiendo ? 'Subiendo imagen…' : form.foto_url ? 'Cambiar foto' : 'Seleccionar foto'}
        </button>

        {form.foto_url && (
          <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src={form.foto_url} alt="" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 8, border: '1px solid #e2e8f0' }} />
            <button className="btn-danger" style={{ padding: '5px 10px', fontSize: 12 }}
              onClick={() => set('foto_url', '')}>Quitar foto</button>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <button onClick={onClose} style={{ flex: 1, padding: 10, background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, fontWeight: 600 }}>
            Cancelar
          </button>
          <button className="btn-primary" style={{ flex: 2 }} disabled={guardando || subiendo} onClick={guardar}>
            {guardando ? 'Guardando…' : modo === 'nuevo' ? 'Agregar producto' : 'Guardar cambios'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ModalBorrar({ item, onClose, onConfirmar }) {
  const [borrando, setBorrando] = useState(false);
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div className="card" style={{ width: '100%', maxWidth: 380, padding: 22 }}>
        <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>¿Eliminar producto?</h3>
        <p style={{ margin: '0 0 18px', fontSize: 13, color: '#64748b' }}>
          Se eliminará <b>{item.nombre_producto}</b> del catálogo. El agente de WhatsApp dejará de ofrecerlo.
        </p>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: 10, background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, fontWeight: 600 }}>Cancelar</button>
          <button className="btn-danger" style={{ flex: 1 }} disabled={borrando}
            onClick={async () => { setBorrando(true); try { await onConfirmar(); onClose(); } catch (e) { setBorrando(false); } }}>
            {borrando ? 'Eliminando…' : 'Sí, eliminar'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Inventario() {
  const { stock, stockLoading, cargarStock, crearStockItem, editarStockItem, eliminarStockItem } = useData();
  const toast = useToast();

  const [busqueda, setBusqueda] = useState('');
  const [sucursal, setSucursal] = useState('');
  const [modalNuevo, setModalNuevo] = useState(false);
  const [modalEditar, setModalEditar] = useState(null);
  const [modalBorrar, setModalBorrar] = useState(null);

  useEffect(() => { cargarStock(); }, [cargarStock]);

  const q = busqueda.trim().toLowerCase();
  const lista = stock.filter(p => {
    const coincide = !q
      || String(p.nombre_producto || '').toLowerCase().includes(q)
      || String(p.categoria || '').toLowerCase().includes(q)
      || String(p.id || '').toLowerCase().includes(q);
    const enSucursal = !sucursal || String(p[sucursal] || '').trim().toUpperCase() === 'SI';
    return coincide && enSucursal;
  });

  const crear = useCallback(async (form) => { await crearStockItem(form); toast.show('Producto agregado'); }, [crearStockItem, toast]);
  const editar = useCallback(async (form) => { await editarStockItem(modalEditar.rowIndex, form); toast.show('Producto actualizado'); }, [editarStockItem, modalEditar, toast]);

  const TH = { padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 };
  const TD = { padding: '10px 12px', verticalAlign: 'middle', fontSize: 13 };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ marginRight: 'auto' }}>
          <h1 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>Inventario</h1>
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
            {stock.length} producto{stock.length !== 1 ? 's' : ''} · lo que edites aquí lo usa el agente de WhatsApp
          </div>
        </div>
        <button onClick={cargarStock} style={{ padding: '9px 14px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13 }}>
          🔄 Sincronizar
        </button>
        <button className="btn-primary" onClick={() => setModalNuevo(true)}>＋ Nuevo producto</button>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <input type="text" value={busqueda} onChange={e => setBusqueda(e.target.value)}
          placeholder="Buscar por nombre, categoría o clave…" style={{ flex: '1 1 240px' }} />
        <select value={sucursal} onChange={e => setSucursal(e.target.value)} style={{ flex: '0 1 200px' }}>
          <option value="">Todas las sucursales</option>
          {SUCURSALES.map(s => <option key={s.key} value={s.key}>Con existencia en {s.label}</option>)}
        </select>
      </div>

      {stockLoading && <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 10 }}>Cargando inventario…</div>}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <th style={TH}>Foto</th>
                <th style={TH}>Producto</th>
                <th style={TH}>Categoría</th>
                <th style={TH}>Precio</th>
                <th style={TH}>MTY</th>
                <th style={TH}>ZAC</th>
                <th style={TH}>QRO</th>
                <th style={{ ...TH, textAlign: 'right' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {lista.length === 0 ? (
                <tr><td colSpan={8} style={{ padding: 30, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                  {stockLoading ? 'Cargando…' : busqueda || sucursal ? 'Sin resultados.' : 'No hay productos en el catálogo.'}
                </td></tr>
              ) : lista.map(p => (
                <tr key={p.rowIndex} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={TD}>
                    {p.foto_url
                      ? <img src={p.foto_url} alt="" style={{ width: 42, height: 42, objectFit: 'cover', borderRadius: 8, display: 'block' }} />
                      : <div style={{ width: 42, height: 42, borderRadius: 8, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1' }}>📷</div>}
                  </td>
                  <td style={{ ...TD, fontWeight: 700, color: '#0f172a' }}>
                    {p.nombre_producto}
                    {p.id && <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>{p.id}</div>}
                  </td>
                  <td style={{ ...TD, color: '#475569' }}>{p.categoria || '—'}</td>
                  <td style={{ ...TD, fontWeight: 700 }}>{p.precio ? `$${Number(p.precio).toLocaleString('es-MX')}` : '—'}</td>
                  <td style={TD}><SiNo value={p.monterrey} /></td>
                  <td style={TD}><SiNo value={p.zacatecas} /></td>
                  <td style={TD}><SiNo value={p.queretaro} /></td>
                  <td style={{ ...TD, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button onClick={() => setModalEditar(p)}
                      style={{ padding: '5px 10px', background: '#eff6ff', color: '#185fa5', border: 'none', borderRadius: 6, fontSize: 12, marginRight: 6 }}>✏️</button>
                    <button onClick={() => setModalBorrar(p)}
                      style={{ padding: '5px 10px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 6, fontSize: 12 }}>🗑️</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modalNuevo && <ModalProducto modo="nuevo" inicial={null} onClose={() => setModalNuevo(false)} onGuardar={crear} />}
      {modalEditar && <ModalProducto modo="editar" inicial={modalEditar} onClose={() => setModalEditar(null)} onGuardar={editar} />}
      {modalBorrar && <ModalBorrar item={modalBorrar} onClose={() => setModalBorrar(null)}
        onConfirmar={async () => { await eliminarStockItem(modalBorrar.rowIndex); toast.show('Producto eliminado'); }} />}
    </div>
  );
}
